import { type SearchAnalyticsEventDto } from "@crowdcoursing/api/openapi";
import { Close } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { type GridColDef } from "@mui/x-data-grid-pro";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { DateTime } from "luxon";
import { memo, useCallback, useMemo, useRef, useState } from "react";

import { type ColumnData } from "~types/datagrid";
import { type DateRangeValue, PopoverDateRangePicker } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { DataGrid } from "~ui/molecules/DataGrid/DataGrid";

type ChartView = "volume" | "keywords" | "filters" | "hourOfDay" | "dayOfWeek";

const chartViewOptions: { label: string; value: ChartView }[] = [
  { label: "Search Volume", value: "volume" },
  { label: "Popular Searches", value: "keywords" },
  { label: "Most Used Filters", value: "filters" },
  { label: "Activity by Hour", value: "hourOfDay" },
  { label: "Activity by Day", value: "dayOfWeek" },
];

const defaultTitle = "Candidate Search Analytics";
const defaultSubtitle = "Track candidate search activity over time";

export interface CandidateSearchAnalyticsProps {
  title?: string;
  subtitle?: string;
  data: SearchAnalyticsEventDto[] | null;
  status: Utils.QueryStatus;
  onTimeRangeChange?: (timeRange: DateRangeValue) => void;
  timeRange?: DateRangeValue;
}

interface SearchVolumeDataPoint {
  date: string;
  count: number;
}

const aggregateSearchesByDay = (events: SearchAnalyticsEventDto[]): SearchVolumeDataPoint[] => {
  const countsByDay: Record<string, number> = {};

  events.forEach((event) => {
    if (event.timestamp) {
      const date = DateTime.fromISO(event.timestamp).toFormat("yyyy-MM-dd");
      countsByDay[date] = (countsByDay[date] || 0) + 1;
    }
  });

  return Object.entries(countsByDay)
    .map(([date, count]) => ({ count, date }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

interface HourOfDayDataPoint {
  hour: number;
  label: string;
  count: number;
}

const aggregateSearchesByHourOfDay = (events: SearchAnalyticsEventDto[]): HourOfDayDataPoint[] => {
  const countsByHour: Record<number, number> = {};

  // Initialize all hours to 0
  for (let i = 0; i < 24; i++) {
    countsByHour[i] = 0;
  }

  events.forEach((event) => {
    if (event.timestamp) {
      const hour = DateTime.fromISO(event.timestamp).hour;
      countsByHour[hour] = (countsByHour[hour] || 0) + 1;
    }
  });

  const formatHour = (hour: number): string => {
    if (hour === 0) {
      return "12am";
    }
    if (hour === 12) {
      return "12pm";
    }
    if (hour < 12) {
      return `${hour}am`;
    }
    return `${hour - 12}pm`;
  };

  return Object.entries(countsByHour)
    .map(([hour, count]) => ({
      count,
      hour: Number(hour),
      label: formatHour(Number(hour)),
    }))
    .sort((a, b) => a.hour - b.hour);
};

interface DayOfWeekDataPoint {
  dayIndex: number;
  label: string;
  count: number;
}

const aggregateSearchesByDayOfWeek = (events: SearchAnalyticsEventDto[]): DayOfWeekDataPoint[] => {
  const countsByDay: Record<number, number> = {};

  // Initialize all days to 0 (1 = Monday, 7 = Sunday in Luxon)
  for (let i = 1; i <= 7; i++) {
    countsByDay[i] = 0;
  }

  events.forEach((event) => {
    if (event.timestamp) {
      const dayOfWeek = DateTime.fromISO(event.timestamp).weekday; // 1 = Monday, 7 = Sunday
      countsByDay[dayOfWeek] = (countsByDay[dayOfWeek] || 0) + 1;
    }
  });

  const dayLabels: Record<number, string> = {
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
    7: "Sunday",
  };

  return Object.entries(countsByDay)
    .map(([day, count]) => ({
      count,
      dayIndex: Number(day),
      label: dayLabels[Number(day)],
    }))
    .sort((a, b) => a.dayIndex - b.dayIndex);
};

const SearchVolumeChart = ({
  data,
  loading,
  onBarClick,
}: {
  data: SearchAnalyticsEventDto[];
  loading: boolean;
  onBarClick?: (date: string) => void;
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const volumeData = useMemo(() => aggregateSearchesByDay(data), [data]);

  const handlePointClick = useCallback(
    (event: Highcharts.PointClickEventObject) => {
      const index = event.point.index;
      const date = volumeData[index]?.date;
      if (date && onBarClick) {
        onBarClick(date);
      }
    },
    [volumeData, onBarClick],
  );

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 300,
        type: "column",
      },
      credits: {
        enabled: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          borderRadius: 4,
          color: "#3b82f6",
          cursor: "pointer",
          point: {
            events: {
              click: handlePointClick,
            },
          },
        },
      },
      series: [
        {
          data: volumeData.map((d) => d.count),
          name: "Searches",
          type: "column",
        },
      ],
      title: {
        text: "Search Volume Over Time",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const date = volumeData[index]?.date;
          const formattedDate = date ? DateTime.fromFormat(date, "yyyy-MM-dd").toFormat("MMM d, yyyy") : "";
          return `<b>${formattedDate}</b><br/>Searches: ${point.y}<br/><em>Click to view details</em>`;
        },
      },
      xAxis: {
        categories: volumeData.map((d) => DateTime.fromFormat(d.date, "yyyy-MM-dd").toFormat("MMM d")),
        title: {
          text: "Date",
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Number of Searches",
        },
      },
    }),
    [volumeData, handlePointClick],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (volumeData.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">No search data available for this period</Typography>
      </Box>
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} updateArgs={[true, true, false]} />
  );
};

const HourOfDayBarChart = ({ data, loading }: { data: SearchAnalyticsEventDto[]; loading: boolean }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const hourData = useMemo(() => aggregateSearchesByHourOfDay(data), [data]);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 300,
        type: "column",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          borderRadius: 4,
          color: "#8b5cf6",
        },
      },
      series: [
        {
          data: hourData.map((d) => d.count),
          name: "Searches",
          type: "column",
        },
      ],
      title: {
        text: "Search Activity by Hour of Day",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const item = hourData[index];
          return `<b>${item?.label}</b><br/>Searches: ${point.y}`;
        },
      },
      xAxis: {
        categories: hourData.map((d) => d.label),
        title: {
          text: "Hour of Day",
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Number of Searches",
        },
      },
    }),
    [hourData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  const hasData = hourData.some((d) => d.count > 0);
  if (!hasData) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">No search data available for this period</Typography>
      </Box>
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} updateArgs={[true, true, false]} />
  );
};

const HourOfDayPieChart = ({ data, loading }: { data: SearchAnalyticsEventDto[]; loading: boolean }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const hourData = useMemo(() => aggregateSearchesByHourOfDay(data), [data]);

  // Filter out hours with 0 searches for pie chart
  const pieData = useMemo(
    () =>
      hourData
        .filter((d) => d.count > 0)
        .map((d) => ({
          name: d.label,
          y: d.count,
        })),
    [hourData],
  );

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 300,
        type: "pie",
      },
      credits: {
        enabled: false,
      },
      legend: {
        align: "right",
        enabled: true,
        layout: "vertical",
        verticalAlign: "middle",
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      series: [
        {
          data: pieData,
          name: "Searches",
          type: "pie",
        },
      ],
      title: {
        text: "Distribution",
      },
      tooltip: {
        pointFormat: "<b>{point.y}</b> searches ({point.percentage:.1f}%)",
      },
    }),
    [pieData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  const hasData = hourData.some((d) => d.count > 0);
  if (!hasData) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">No search data available for this period</Typography>
      </Box>
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} updateArgs={[true, true, false]} />
  );
};

const DayOfWeekBarChart = ({ data, loading }: { data: SearchAnalyticsEventDto[]; loading: boolean }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const dayData = useMemo(() => aggregateSearchesByDayOfWeek(data), [data]);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 300,
        type: "column",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          borderRadius: 4,
          color: "#10b981",
        },
      },
      series: [
        {
          data: dayData.map((d) => d.count),
          name: "Searches",
          type: "column",
        },
      ],
      title: {
        text: "Search Activity by Day of Week",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const item = dayData[index];
          return `<b>${item?.label}</b><br/>Searches: ${point.y}`;
        },
      },
      xAxis: {
        categories: dayData.map((d) => d.label),
        title: {
          text: "Day of Week",
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Number of Searches",
        },
      },
    }),
    [dayData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  const hasData = dayData.some((d) => d.count > 0);
  if (!hasData) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">No search data available for this period</Typography>
      </Box>
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} updateArgs={[true, true, false]} />
  );
};

const DayOfWeekPieChart = ({ data, loading }: { data: SearchAnalyticsEventDto[]; loading: boolean }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const dayData = useMemo(() => aggregateSearchesByDayOfWeek(data), [data]);

  // Filter out days with 0 searches for pie chart
  const pieData = useMemo(
    () =>
      dayData
        .filter((d) => d.count > 0)
        .map((d) => ({
          name: d.label,
          y: d.count,
        })),
    [dayData],
  );

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 300,
        type: "pie",
      },
      credits: {
        enabled: false,
      },
      legend: {
        align: "right",
        enabled: true,
        layout: "vertical",
        verticalAlign: "middle",
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      series: [
        {
          data: pieData,
          name: "Searches",
          type: "pie",
        },
      ],
      title: {
        text: "Distribution",
      },
      tooltip: {
        pointFormat: "<b>{point.y}</b> searches ({point.percentage:.1f}%)",
      },
    }),
    [pieData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  const hasData = dayData.some((d) => d.count > 0);
  if (!hasData) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">No search data available for this period</Typography>
      </Box>
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} updateArgs={[true, true, false]} />
  );
};

interface HourTableRow {
  rowId: string;
  label: string;
  count: number;
}

const HourOfDayTable = ({ data, loading }: { data: SearchAnalyticsEventDto[]; loading: boolean }) => {
  const hourData = useMemo(() => aggregateSearchesByHourOfDay(data), [data]);

  const columns: GridColDef<HourTableRow>[] = [
    { field: "label", headerName: "Hour", minWidth: 120, width: 150 },
    { field: "count", headerName: "Searches", minWidth: 100, width: 120 },
  ];

  const rows: HourTableRow[] = hourData.map((d) => ({
    count: d.count,
    label: d.label,
    rowId: `hour-${d.hour}`,
  }));

  return (
    <Box>
      <Typography sx={{ mb: 1 }} variant="subtitle2">
        Searches by Hour
      </Typography>
      <DataGrid<HourTableRow>
        columns={columns as ColumnData<HourTableRow>[]}
        rows={rows}
        status={loading ? "pending" : "success"}
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        rowHeight={40}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
          sorting: {
            sortModel: [{ field: "count", sort: "desc" }],
          },
        }}
        pageSizeOptions={[5, 10, 24]}
        paginationMode="client"
        sortingMode="client"
        slots={{
          toolbar: () => null,
        }}
        slotProps={{
          container: {
            sx: { height: "auto", maxHeight: 350, minHeight: "unset" },
          },
        }}
      />
    </Box>
  );
};

interface DayTableRow {
  rowId: string;
  label: string;
  count: number;
}

const DayOfWeekTable = ({ data, loading }: { data: SearchAnalyticsEventDto[]; loading: boolean }) => {
  const dayData = useMemo(() => aggregateSearchesByDayOfWeek(data), [data]);

  const columns: GridColDef<DayTableRow>[] = [
    { field: "label", headerName: "Day", minWidth: 120, width: 150 },
    { field: "count", headerName: "Searches", minWidth: 100, width: 120 },
  ];

  const rows: DayTableRow[] = dayData.map((d) => ({
    count: d.count,
    label: d.label,
    rowId: `day-${d.dayIndex}`,
  }));

  return (
    <Box>
      <Typography sx={{ mb: 1 }} variant="subtitle2">
        Searches by Day
      </Typography>
      <DataGrid<DayTableRow>
        columns={columns as ColumnData<DayTableRow>[]}
        rows={rows}
        status={loading ? "pending" : "success"}
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        rowHeight={40}
        initialState={{
          sorting: {
            sortModel: [{ field: "count", sort: "desc" }],
          },
        }}
        sortingMode="client"
        slots={{
          toolbar: () => null,
        }}
        slotProps={{
          container: {
            sx: { height: "auto", maxHeight: 350, minHeight: "unset" },
          },
        }}
      />
    </Box>
  );
};

// Aggregate keywords across all searches to find popular ones
interface KeywordCount {
  keyword: string;
  count: number;
  type: "skill" | "certification" | "keyword";
}

const aggregateKeywords = (events: SearchAnalyticsEventDto[]): KeywordCount[] => {
  // Track counts per name per type - determined PER SEARCH
  // A search with only skill:X counts as Skill
  // A search with only cert:X counts as Certification
  // A search with BOTH skill:X and cert:X counts as Keyword
  const skillCounts: Record<string, number> = {};
  const certCounts: Record<string, number> = {};
  const keywordCounts: Record<string, number> = {};
  const otherKeywords: Record<string, number> = {};

  events.forEach((event) => {
    if (!event.keywords) {
      return;
    }

    // Track what we've seen in THIS event
    const seenSkills = new Set<string>();
    const seenCerts = new Set<string>();

    event.keywords.forEach((keyword) => {
      if (keyword.startsWith("skill:")) {
        seenSkills.add(keyword.substring(6));
      } else if (keyword.startsWith("cert:")) {
        seenCerts.add(keyword.substring(5));
      } else if (keyword.startsWith("certification:")) {
        seenCerts.add(keyword.substring(14));
      } else if (!keyword.match(/^graduationYear[<>]=?\d+$/) && !keyword.match(/^gpaWeighted[<>]=?[\d.]+$/)) {
        // Skip filter expressions, count everything else
        otherKeywords[keyword] = (otherKeywords[keyword] || 0) + 1;
      }
    });

    // For each name in this event, determine its type based on THIS search
    const allNamesInEvent = new Set([...seenSkills, ...seenCerts]);

    allNamesInEvent.forEach((name) => {
      const isSkill = seenSkills.has(name);
      const isCert = seenCerts.has(name);

      if (isSkill && isCert) {
        // This specific search has both skill:X and cert:X - it's a keyword search
        keywordCounts[name] = (keywordCounts[name] || 0) + 1;
      } else if (isSkill) {
        // This search only has skill:X
        skillCounts[name] = (skillCounts[name] || 0) + 1;
      } else if (isCert) {
        // This search only has cert:X
        certCounts[name] = (certCounts[name] || 0) + 1;
      }
    });
  });

  const results: KeywordCount[] = [];

  // Add keyword entries (searches that had both skill:X and cert:X)
  Object.entries(keywordCounts).forEach(([name, count]) => {
    results.push({ count, keyword: name, type: "keyword" });
  });

  // Add skill entries (searches that only had skill:X)
  Object.entries(skillCounts).forEach(([name, count]) => {
    results.push({ count, keyword: name, type: "skill" });
  });

  // Add cert entries (searches that only had cert:X)
  Object.entries(certCounts).forEach(([name, count]) => {
    results.push({ count, keyword: name, type: "certification" });
  });

  // Add other keywords
  Object.entries(otherKeywords).forEach(([keyword, count]) => {
    results.push({ count, keyword, type: "keyword" });
  });

  // Sort by count descending
  return results.sort((a, b) => b.count - a.count);
};

type KeywordLimit = 10 | 25 | 50 | "all";

const PopularKeywordsChart = memo(function PopularKeywordsChart({
  data,
  limit,
  loading,
  onBarClick,
}: {
  data: SearchAnalyticsEventDto[];
  limit: KeywordLimit;
  loading: boolean;
  onBarClick?: (keyword: string, type: "skill" | "certification" | "keyword") => void;
}) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const allKeywordData = useMemo(() => aggregateKeywords(data), [data]);
  const keywordData = useMemo(
    () => (limit === "all" ? allKeywordData : allKeywordData.slice(0, limit)),
    [allKeywordData, limit],
  );

  // Dynamic height based on number of items
  const chartHeight = Math.max(300, keywordData.length * 25);

  const getColorForType = (type: "skill" | "certification" | "keyword"): string => {
    switch (type) {
      case "skill":
        return "#3b82f6"; // Blue
      case "certification":
        return "#8b5cf6"; // Purple
      case "keyword":
        return "#10b981"; // Green
      default:
        return "#6b7280"; // Gray
    }
  };

  const handlePointClick = useCallback(
    (event: Highcharts.PointClickEventObject) => {
      const index = event.point.index;
      const item = keywordData[index];
      if (item && onBarClick) {
        onBarClick(item.keyword, item.type);
      }
    },
    [keywordData, onBarClick],
  );

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: chartHeight,
        type: "bar",
      },
      credits: {
        enabled: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          animation: false,
          borderRadius: 4,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
          },
          point: {
            events: {
              click: handlePointClick,
            },
          },
        },
      },
      series: [
        {
          colorByPoint: true,
          colors: keywordData.map((d) => getColorForType(d.type)),
          data: keywordData.map((d) => d.count),
          name: "Searches",
          type: "bar",
        },
      ],
      title: {
        text: "Popular Searches",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const item = keywordData[index];
          const typeLabel =
            item?.type === "skill" ? "Skill" : item?.type === "certification" ? "Certification" : "Keyword";
          return `<b>${item?.keyword}</b><br/>${typeLabel}<br/>Searches: ${point.y}<br/><em>Click to view details</em>`;
        },
      },
      xAxis: {
        categories: keywordData.map((d) => d.keyword),
        title: {
          text: null,
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Number of Searches",
        },
      },
    }),
    [chartHeight, handlePointClick, keywordData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (keywordData.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">No keyword data available for this period</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} updateArgs={[true, true, false]} />
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 1 }}>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
          <Box sx={{ backgroundColor: "#3b82f6", borderRadius: 1, height: 12, width: 12 }} />
          <Typography variant="caption">Skills</Typography>
        </Box>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
          <Box sx={{ backgroundColor: "#8b5cf6", borderRadius: 1, height: 12, width: 12 }} />
          <Typography variant="caption">Certifications</Typography>
        </Box>
        <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
          <Box sx={{ backgroundColor: "#10b981", borderRadius: 1, height: 12, width: 12 }} />
          <Typography variant="caption">Keywords</Typography>
        </Box>
      </Box>
    </Box>
  );
});

// Aggregate filter usage across all searches
type FilterType = "graduationYear" | "gpa" | "skills" | "certifications";

interface FilterUsageCount {
  filterType: FilterType;
  label: string;
  count: number;
  color: string;
}

const aggregateFilterUsage = (events: SearchAnalyticsEventDto[]): FilterUsageCount[] => {
  const counts = {
    certifications: 0,
    gpa: 0,
    graduationYear: 0,
    skills: 0,
  };

  events.forEach((event) => {
    if (!event.keywords) {
      return;
    }

    let hasGradYear = false;
    let hasGpa = false;
    let hasSkills = false;
    let hasCerts = false;

    event.keywords.forEach((keyword) => {
      if (keyword.match(/^graduationYear[<>]=?\d+$/)) {
        hasGradYear = true;
      } else if (keyword.match(/^gpaWeighted[<>]=?[\d.]+$/)) {
        hasGpa = true;
      } else if (keyword.startsWith("skill:")) {
        hasSkills = true;
      } else if (keyword.startsWith("cert:") || keyword.startsWith("certification:")) {
        hasCerts = true;
      }
    });

    if (hasGradYear) {
      counts.graduationYear++;
    }
    if (hasGpa) {
      counts.gpa++;
    }
    if (hasSkills) {
      counts.skills++;
    }
    if (hasCerts) {
      counts.certifications++;
    }
  });

  const results: FilterUsageCount[] = [
    { color: "#f59e0b", count: counts.graduationYear, filterType: "graduationYear", label: "Graduation Year" },
    { color: "#ef4444", count: counts.gpa, filterType: "gpa", label: "GPA" },
    { color: "#3b82f6", count: counts.skills, filterType: "skills", label: "Skills" },
    { color: "#8b5cf6", count: counts.certifications, filterType: "certifications", label: "Certifications" },
  ];

  return results.sort((a, b) => b.count - a.count);
};

const MostUsedFiltersChart = ({
  data,
  loading,
  onBarClick,
}: {
  data: SearchAnalyticsEventDto[];
  loading: boolean;
  onBarClick?: (filterType: FilterType) => void;
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const filterData = useMemo(() => aggregateFilterUsage(data), [data]);

  const handlePointClick = useCallback(
    (event: Highcharts.PointClickEventObject) => {
      const index = event.point.index;
      const item = filterData[index];
      if (item && onBarClick) {
        onBarClick(item.filterType);
      }
    },
    [filterData, onBarClick],
  );

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 300,
        type: "bar",
      },
      credits: {
        enabled: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
          },
          point: {
            events: {
              click: handlePointClick,
            },
          },
        },
      },
      series: [
        {
          colorByPoint: true,
          colors: filterData.map((d) => d.color),
          data: filterData.map((d) => d.count),
          name: "Searches",
          type: "bar",
        },
      ],
      title: {
        text: "Most Used Filters",
      },
      tooltip: {
        formatter: function () {
          const point = this.point;
          const index = point.index;
          const item = filterData[index];
          return `<b>${item?.label}</b><br/>Used in ${point.y} search${point.y !== 1 ? "es" : ""}<br/><em>Click to view details</em>`;
        },
      },
      xAxis: {
        categories: filterData.map((d) => d.label),
        title: {
          text: null,
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Number of Searches",
        },
      },
    }),
    [filterData, handlePointClick],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  const hasAnyFilters = filterData.some((d) => d.count > 0);

  if (!hasAnyFilters) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
        <Typography color="text.secondary">No filter usage data available for this period</Typography>
      </Box>
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} updateArgs={[true, true, false]} />
  );
};

// Aggregate individual skills and certifications with their counts
interface SkillCertCount {
  rowId: string;
  name: string;
  type: "Skill" | "Certification" | "Keyword";
  count: number;
}

const aggregateSkillsAndCerts = (events: SearchAnalyticsEventDto[]): SkillCertCount[] => {
  // Track counts per name per type - determined PER SEARCH, not globally
  // A search with only skill:AP counts as Skill
  // A search with only cert:AP counts as Certification
  // A search with BOTH skill:AP and cert:AP counts as Keyword
  const skillCounts: Record<string, number> = {};
  const certCounts: Record<string, number> = {};
  const keywordCounts: Record<string, number> = {};

  events.forEach((event) => {
    if (!event.keywords) {
      return;
    }

    // Track what we've seen in THIS event
    const seenSkills = new Set<string>();
    const seenCerts = new Set<string>();

    event.keywords.forEach((keyword) => {
      if (keyword.startsWith("skill:")) {
        seenSkills.add(keyword.substring(6));
      } else if (keyword.startsWith("cert:")) {
        seenCerts.add(keyword.substring(5));
      } else if (keyword.startsWith("certification:")) {
        seenCerts.add(keyword.substring(14));
      }
    });

    // For each name in this event, determine its type based on THIS search
    const allNamesInEvent = new Set([...seenSkills, ...seenCerts]);

    allNamesInEvent.forEach((name) => {
      const isSkill = seenSkills.has(name);
      const isCert = seenCerts.has(name);

      if (isSkill && isCert) {
        // This specific search has both skill:X and cert:X - it's a keyword search
        keywordCounts[name] = (keywordCounts[name] || 0) + 1;
      } else if (isSkill) {
        // This search only has skill:X
        skillCounts[name] = (skillCounts[name] || 0) + 1;
      } else if (isCert) {
        // This search only has cert:X
        certCounts[name] = (certCounts[name] || 0) + 1;
      }
    });
  });

  const results: SkillCertCount[] = [];

  // Add keyword entries
  Object.entries(keywordCounts).forEach(([name, count]) => {
    results.push({
      count,
      name,
      rowId: `keyword-${name}`,
      type: "Keyword",
    });
  });

  // Add skill entries
  Object.entries(skillCounts).forEach(([name, count]) => {
    results.push({
      count,
      name,
      rowId: `skill-${name}`,
      type: "Skill",
    });
  });

  // Add cert entries
  Object.entries(certCounts).forEach(([name, count]) => {
    results.push({
      count,
      name,
      rowId: `cert-${name}`,
      type: "Certification",
    });
  });

  return results.sort((a, b) => b.count - a.count);
};

const TYPE_OPTIONS = ["Skill", "Certification", "Keyword"] as const;

const SKILLS_TABLE_NAMES_STORAGE_KEY = "candidateSearchAnalytics.skillsTable.selectedNames";
const SKILLS_TABLE_TYPES_STORAGE_KEY = "candidateSearchAnalytics.skillsTable.selectedTypes";

const SkillsCertsTable = ({ data, loading }: { data: SearchAnalyticsEventDto[]; loading: boolean }) => {
  const tableData = useMemo(() => aggregateSkillsAndCerts(data), [data]);

  // Get unique names for filtering
  const nameOptions = useMemo(() => [...new Set(tableData.map((row) => row.name))].sort(), [tableData]);

  // Initialize filter state from localStorage, but only keep values that exist in current data
  const [selectedNames, setSelectedNames] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(SKILLS_TABLE_NAMES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [selectedTypes, setSelectedTypes] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(SKILLS_TABLE_TYPES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      // Only keep valid type options
      return parsed.filter((t: string) => TYPE_OPTIONS.includes(t as (typeof TYPE_OPTIONS)[number]));
    } catch {
      return [];
    }
  });

  // Filter the table data based on selections
  const filteredData = useMemo(() => {
    // Only filter by names that exist in current data
    const validNames = selectedNames.filter((name) => nameOptions.includes(name));
    return tableData.filter((row) => {
      const nameMatch = validNames.length === 0 || validNames.includes(row.name);
      const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(row.type);
      return nameMatch && typeMatch;
    });
  }, [tableData, selectedNames, selectedTypes, nameOptions]);

  const handleNameChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const newNames = typeof value === "string" ? value.split(",") : value;
    setSelectedNames(newNames);
    localStorage.setItem(SKILLS_TABLE_NAMES_STORAGE_KEY, JSON.stringify(newNames));
  };

  const handleTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const newTypes = typeof value === "string" ? value.split(",") : value;
    setSelectedTypes(newTypes);
    localStorage.setItem(SKILLS_TABLE_TYPES_STORAGE_KEY, JSON.stringify(newTypes));
  };

  const columns = useMemo<GridColDef<SkillCertCount>[]>(
    () => [
      {
        field: "name",
        flex: 2,
        headerName: "Name",
        minWidth: 150,
      },
      {
        field: "type",
        flex: 1,
        headerName: "Type",
        minWidth: 100,
      },
      {
        align: "right",
        field: "count",
        flex: 1,
        headerAlign: "right",
        headerName: "Search Count",
        minWidth: 100,
      },
    ],
    [],
  );

  if (loading) {
    return null;
  }

  if (tableData.length === 0) {
    return (
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography color="text.secondary" variant="body2">
          No skills or certifications searched in this period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, overflow: "hidden", width: "100%" }}>
      <Typography sx={{ mb: 1 }} variant="subtitle2">
        Skills & Certifications Breakdown
      </Typography>

      {/* Filter controls */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="name-filter-label">Filter by Name</InputLabel>
          <Select
            labelId="name-filter-label"
            id="name-filter"
            multiple
            value={selectedNames}
            onChange={handleNameChange}
            input={<OutlinedInput label="Filter by Name" />}
            renderValue={(selected) => (selected.length === 0 ? "" : `${selected.length} selected`)}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {nameOptions.map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={selectedNames.includes(name)} size="small" />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="type-filter-label">Filter by Type</InputLabel>
          <Select
            labelId="type-filter-label"
            id="type-filter"
            multiple
            value={selectedTypes}
            onChange={handleTypeChange}
            input={<OutlinedInput label="Filter by Type" />}
            renderValue={(selected) => (selected.length === 0 ? "" : `${selected.length} selected`)}
          >
            {TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                <Checkbox checked={selectedTypes.includes(type)} size="small" />
                <ListItemText primary={type} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {(selectedNames.length > 0 || selectedTypes.length > 0) && (
          <Box sx={{ alignItems: "center", display: "flex" }}>
            <Chip
              label="Clear filters"
              onDelete={() => {
                setSelectedNames([]);
                setSelectedTypes([]);
                localStorage.removeItem(SKILLS_TABLE_NAMES_STORAGE_KEY);
                localStorage.removeItem(SKILLS_TABLE_TYPES_STORAGE_KEY);
              }}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </Box>

      <Box sx={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}>
        <DataGrid<SkillCertCount>
          columns={columns as ColumnData<SkillCertCount>[]}
          rows={filteredData}
          status={loading ? "pending" : "success"}
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          rowHeight={40}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          paginationMode="client"
          sortingMode="client"
          slots={{
            toolbar: () => null,
          }}
          slotProps={{
            container: {
              sx: { height: "auto", maxHeight: 400, minHeight: "unset" },
            },
            empty: {
              message: "No skills or certifications found",
            },
          }}
        />
      </Box>
    </Box>
  );
};

// Storage keys for individual type tables
const SKILLS_ONLY_TABLE_NAMES_KEY = "candidateSearchAnalytics.skillsOnlyTable.selectedNames";
const CERTS_ONLY_TABLE_NAMES_KEY = "candidateSearchAnalytics.certsOnlyTable.selectedNames";
const KEYWORDS_ONLY_TABLE_NAMES_KEY = "candidateSearchAnalytics.keywordsOnlyTable.selectedNames";

interface SingleTypeTableProps {
  data: SearchAnalyticsEventDto[];
  loading: boolean;
  title: string;
  type: "Skill" | "Certification" | "Keyword";
  storageKey: string;
}

const SingleTypeTable = ({ data, loading, storageKey, title, type }: SingleTypeTableProps) => {
  // Filter the aggregated data to only show the specified type
  const tableData = useMemo(() => {
    const allData = aggregateSkillsAndCerts(data);
    return allData.filter((row) => row.type === type);
  }, [data, type]);

  // Get unique names for filtering
  const nameOptions = useMemo(() => [...new Set(tableData.map((row) => row.name))].sort(), [tableData]);

  // Initialize filter state from localStorage
  const [selectedNames, setSelectedNames] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Filter the table data based on selections
  const filteredData = useMemo(() => {
    const validNames = selectedNames.filter((name) => nameOptions.includes(name));
    if (validNames.length === 0) {
      return tableData;
    }
    return tableData.filter((row) => validNames.includes(row.name));
  }, [tableData, selectedNames, nameOptions]);

  const handleNameChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const newNames = typeof value === "string" ? value.split(",") : value;
    setSelectedNames(newNames);
    localStorage.setItem(storageKey, JSON.stringify(newNames));
  };

  const columns = useMemo<GridColDef<SkillCertCount>[]>(
    () => [
      {
        field: "name",
        flex: 2,
        headerName: "Name",
        minWidth: 120,
      },
      {
        align: "right",
        field: "count",
        flex: 1,
        headerAlign: "right",
        headerName: "Count",
        minWidth: 70,
      },
    ],
    [],
  );

  if (loading) {
    return null;
  }

  if (tableData.length === 0) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography sx={{ mb: 1 }} variant="h6">
          {title}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          No {type.toLowerCase()}s searched in this period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: "hidden", width: "100%" }}>
      <Typography sx={{ mb: 1 }} variant="h6">
        {title}
      </Typography>

      {/* Filter control */}
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel id={`${type}-name-filter-label`}>Filter by Name</InputLabel>
          <Select
            id={`${type}-name-filter`}
            input={<OutlinedInput label="Filter by Name" />}
            labelId={`${type}-name-filter-label`}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
            multiple
            onChange={handleNameChange}
            renderValue={(selected) => (selected.length === 0 ? "" : `${selected.length} selected`)}
            value={selectedNames}
          >
            {nameOptions.map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={selectedNames.includes(name)} size="small" />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedNames.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label="Clear filter"
              onDelete={() => {
                setSelectedNames([]);
                localStorage.removeItem(storageKey);
              }}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </Box>

      <Box sx={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}>
        <DataGrid<SkillCertCount>
          columns={columns as ColumnData<SkillCertCount>[]}
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          paginationMode="client"
          rowHeight={40}
          rows={filteredData}
          slotProps={{
            container: {
              sx: { height: "auto", maxHeight: 400, minHeight: "unset" },
            },
            empty: {
              message: `No ${type.toLowerCase()}s found`,
            },
          }}
          slots={{
            toolbar: () => null,
          }}
          sortingMode="client"
          status={loading ? "pending" : "success"}
        />
      </Box>
    </Box>
  );
};

// Parse keywords to extract structured filter information
interface ParsedSearchFilters {
  skills: string[];
  certifications: string[];
  graduationYearMin: number | null;
  graduationYearMax: number | null;
  gpaMin: number | null;
  gpaMax: number | null;
  otherKeywords: string[];
}

const parseKeywords = (keywords: string[] | null | undefined): ParsedSearchFilters => {
  const result: ParsedSearchFilters = {
    certifications: [],
    gpaMax: null,
    gpaMin: null,
    graduationYearMax: null,
    graduationYearMin: null,
    otherKeywords: [],
    skills: [],
  };

  if (!keywords) {
    return result;
  }

  // First pass: collect all values
  const tempSkills: string[] = [];
  const tempCerts: string[] = [];

  keywords.forEach((keyword) => {
    // Parse skill:X
    if (keyword.startsWith("skill:")) {
      tempSkills.push(keyword.substring(6));
      return;
    }

    // Parse cert:X or certification:X
    if (keyword.startsWith("cert:")) {
      tempCerts.push(keyword.substring(5));
      return;
    }
    if (keyword.startsWith("certification:")) {
      tempCerts.push(keyword.substring(14));
      return;
    }

    // Parse graduationYear>X or graduationYear>=X
    const gradYearMinMatch = keyword.match(/^graduationYear[>]=?(\d+)$/);
    if (gradYearMinMatch) {
      result.graduationYearMin = parseInt(gradYearMinMatch[1], 10);
      return;
    }

    // Parse graduationYear<X or graduationYear<=X
    const gradYearMaxMatch = keyword.match(/^graduationYear[<]=?(\d+)$/);
    if (gradYearMaxMatch) {
      result.graduationYearMax = parseInt(gradYearMaxMatch[1], 10);
      return;
    }

    // Parse gpaWeighted>X or gpaWeighted>=X
    const gpaMinMatch = keyword.match(/^gpaWeighted[>]=?([\d.]+)$/);
    if (gpaMinMatch) {
      result.gpaMin = parseFloat(gpaMinMatch[1]);
      return;
    }

    // Parse gpaWeighted<X or gpaWeighted<=X
    const gpaMaxMatch = keyword.match(/^gpaWeighted[<]=?([\d.]+)$/);
    if (gpaMaxMatch) {
      result.gpaMax = parseFloat(gpaMaxMatch[1]);
      return;
    }

    // Everything else is a regular keyword
    result.otherKeywords.push(keyword);
  });

  // Second pass: if a value appears in both skills and certs, it's a keyword search
  // Move those to otherKeywords and only keep unique ones in their respective sections
  const skillSet = new Set(tempSkills);
  const certSet = new Set(tempCerts);

  tempSkills.forEach((skill) => {
    if (certSet.has(skill)) {
      // Same value in both - it's a keyword search
      if (!result.otherKeywords.includes(skill)) {
        result.otherKeywords.push(skill);
      }
    } else {
      // Unique to skills
      result.skills.push(skill);
    }
  });

  tempCerts.forEach((cert) => {
    if (skillSet.has(cert)) {
      // Already handled above - skip
      return;
    }
    // Unique to certifications
    result.certifications.push(cert);
  });

  return result;
};

const hasFilters = (filters: ParsedSearchFilters): boolean => {
  return (
    filters.skills.length > 0 ||
    filters.certifications.length > 0 ||
    filters.graduationYearMin !== null ||
    filters.graduationYearMax !== null ||
    filters.gpaMin !== null ||
    filters.gpaMax !== null
  );
};

// Slideout component to show search details for a selected date
interface SearchDetailsSlideoutProps {
  open: boolean;
  onClose: () => void;
  selectedDate: string | null;
  searches: SearchAnalyticsEventDto[];
}

const SearchDetailsSlideout = ({ open, onClose, selectedDate, searches }: SearchDetailsSlideoutProps) => {
  const formattedDate = selectedDate ? DateTime.fromFormat(selectedDate, "yyyy-MM-dd").toFormat("MMMM d, yyyy") : "";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: "100%", width: 420 },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6">Search Details</Typography>
            <Typography color="text.secondary" variant="body2">
              {formattedDate} • {searches.length} search{searches.length !== 1 ? "es" : ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {searches.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
              No searches found for this date
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {searches.map((search, index) => {
                const filters = parseKeywords(search.keywords);
                const showFilters = hasFilters(filters);

                return (
                  <Paper key={search.id ?? index} elevation={1} sx={{ p: 2 }}>
                    {/* Filters Applied (parsed from keywords) */}
                    {showFilters && (
                      <>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Filters Applied
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                          {/* Graduation Year Range */}
                          {(filters.graduationYearMin !== null || filters.graduationYearMax !== null) && (
                            <Typography variant="body2">
                              Graduation Year: {filters.graduationYearMin ?? "Any"} -{" "}
                              {filters.graduationYearMax ?? "Any"}
                            </Typography>
                          )}

                          {/* GPA Range */}
                          {(filters.gpaMin !== null || filters.gpaMax !== null) && (
                            <Typography variant="body2">
                              GPA: {filters.gpaMin !== null ? filters.gpaMin.toFixed(2) : "Any"} -{" "}
                              {filters.gpaMax !== null ? filters.gpaMax.toFixed(2) : "Any"}
                            </Typography>
                          )}

                          {/* Skills */}
                          {filters.skills.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                Skills:
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {filters.skills.map((skill, i) => (
                                  <Chip key={i} label={skill} size="small" color="primary" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Certifications */}
                          {filters.certifications.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                Certifications:
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {filters.certifications.map((cert, i) => (
                                  <Chip key={i} label={cert} size="small" color="secondary" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {/* Other Keywords (anything that wasn't parsed as a filter) */}
                    {filters.otherKeywords.length > 0 && (
                      <>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Keywords
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                          {filters.otherKeywords.map((keyword, i) => (
                            <Chip key={i} label={keyword} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </>
                    )}

                    {/* Show message if no filters or keywords */}
                    {!showFilters && filters.otherKeywords.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No filters applied
                      </Typography>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    {/* Timestamp */}
                    <Typography variant="caption" color="text.secondary">
                      {search.timestamp ? DateTime.fromISO(search.timestamp).toFormat("h:mm a") : "Unknown time"}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

const getSearchesByDate = (data: SearchAnalyticsEventDto[], date: string): SearchAnalyticsEventDto[] => {
  return data.filter((event) => {
    if (!event.timestamp) {
      return false;
    }
    const eventDate = DateTime.fromISO(event.timestamp).toFormat("yyyy-MM-dd");
    return eventDate === date;
  });
};

const getSearchesByKeyword = (
  data: SearchAnalyticsEventDto[],
  keyword: string,
  type: "skill" | "certification" | "keyword",
): SearchAnalyticsEventDto[] => {
  return data.filter((event) => {
    if (!event.keywords) {
      return false;
    }

    return event.keywords.some((kw) => {
      if (type === "skill") {
        return kw === `skill:${keyword}`;
      } else if (type === "certification") {
        return kw === `cert:${keyword}` || kw === `certification:${keyword}`;
      } else {
        // For keyword type, check if it's in both skill and cert OR is a standalone keyword
        const isInSkill = event.keywords?.includes(`skill:${keyword}`);
        const isInCert =
          event.keywords?.includes(`cert:${keyword}`) || event.keywords?.includes(`certification:${keyword}`);
        const isStandalone = kw === keyword;
        return (isInSkill && isInCert) || isStandalone;
      }
    });
  });
};

const getSearchesByFilterType = (
  data: SearchAnalyticsEventDto[],
  filterType: FilterType,
): SearchAnalyticsEventDto[] => {
  return data.filter((event) => {
    if (!event.keywords) {
      return false;
    }

    return event.keywords.some((kw) => {
      switch (filterType) {
        case "graduationYear":
          return kw.match(/^graduationYear[<>]=?\d+$/);
        case "gpa":
          return kw.match(/^gpaWeighted[<>]=?[\d.]+$/);
        case "skills":
          return kw.startsWith("skill:");
        case "certifications":
          return kw.startsWith("cert:") || kw.startsWith("certification:");
        default:
          return false;
      }
    });
  });
};

// Slideout component to show filter details
interface FilterDetailsSlideoutProps {
  open: boolean;
  onClose: () => void;
  filterType: FilterType | null;
  searches: SearchAnalyticsEventDto[];
}

const getFilterTypeLabel = (filterType: FilterType | null): string => {
  switch (filterType) {
    case "graduationYear":
      return "Graduation Year";
    case "gpa":
      return "GPA";
    case "skills":
      return "Skills";
    case "certifications":
      return "Certifications";
    default:
      return "Filter";
  }
};

const FilterDetailsSlideout = ({ open, onClose, filterType, searches }: FilterDetailsSlideoutProps) => {
  const label = getFilterTypeLabel(filterType);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: "100%", width: 420 },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6">{label} Filter</Typography>
            <Typography color="text.secondary" variant="body2">
              Used in {searches.length} search{searches.length !== 1 ? "es" : ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {searches.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
              No searches found using this filter
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {searches.map((search, index) => {
                const filters = parseKeywords(search.keywords);
                const showFilters = hasFilters(filters);

                return (
                  <Paper key={search.id ?? index} elevation={1} sx={{ p: 2 }}>
                    {showFilters && (
                      <>
                        <Typography color="text.secondary" gutterBottom variant="subtitle2">
                          Filters Applied
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                          {(filters.graduationYearMin !== null || filters.graduationYearMax !== null) && (
                            <Typography variant="body2">
                              Graduation Year: {filters.graduationYearMin ?? "Any"} -{" "}
                              {filters.graduationYearMax ?? "Any"}
                            </Typography>
                          )}
                          {(filters.gpaMin !== null || filters.gpaMax !== null) && (
                            <Typography variant="body2">
                              GPA: {filters.gpaMin !== null ? filters.gpaMin.toFixed(2) : "Any"} -{" "}
                              {filters.gpaMax !== null ? filters.gpaMax.toFixed(2) : "Any"}
                            </Typography>
                          )}
                          {filters.skills.length > 0 && (
                            <Box>
                              <Typography sx={{ mb: 0.5 }} variant="body2">
                                Skills:
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {filters.skills.map((skill, i) => (
                                  <Chip key={i} color="primary" label={skill} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                          {filters.certifications.length > 0 && (
                            <Box>
                              <Typography sx={{ mb: 0.5 }} variant="body2">
                                Certifications:
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {filters.certifications.map((cert, i) => (
                                  <Chip key={i} color="secondary" label={cert} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {filters.otherKeywords.length > 0 && (
                      <>
                        <Typography color="text.secondary" gutterBottom variant="subtitle2">
                          Keywords
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                          {filters.otherKeywords.map((kw, i) => (
                            <Chip key={i} label={kw} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </>
                    )}

                    {!showFilters && filters.otherKeywords.length === 0 && (
                      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                        No other filters applied
                      </Typography>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Typography color="text.secondary" variant="caption">
                      {search.timestamp
                        ? DateTime.fromISO(search.timestamp).toFormat("MMM d, yyyy 'at' h:mm a")
                        : "Unknown time"}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

// Slideout component to show keyword details
interface KeywordDetailsSlideoutProps {
  open: boolean;
  onClose: () => void;
  keyword: string | null;
  keywordType: "skill" | "certification" | "keyword" | null;
  searches: SearchAnalyticsEventDto[];
}

const KeywordDetailsSlideout = ({ open, onClose, keyword, keywordType, searches }: KeywordDetailsSlideoutProps) => {
  const typeLabel = keywordType === "skill" ? "Skill" : keywordType === "certification" ? "Certification" : "Keyword";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: "100%", width: 420 },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6">&ldquo;{keyword}&rdquo;</Typography>
            <Typography color="text.secondary" variant="body2">
              {typeLabel} • {searches.length} search{searches.length !== 1 ? "es" : ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {searches.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
              No searches found for this keyword
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {searches.map((search, index) => {
                const filters = parseKeywords(search.keywords);
                const showFilters = hasFilters(filters);

                return (
                  <Paper key={search.id ?? index} elevation={1} sx={{ p: 2 }}>
                    {/* Filters Applied (parsed from keywords) */}
                    {showFilters && (
                      <>
                        <Typography color="text.secondary" gutterBottom variant="subtitle2">
                          Filters Applied
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                          {(filters.graduationYearMin !== null || filters.graduationYearMax !== null) && (
                            <Typography variant="body2">
                              Graduation Year: {filters.graduationYearMin ?? "Any"} -{" "}
                              {filters.graduationYearMax ?? "Any"}
                            </Typography>
                          )}
                          {(filters.gpaMin !== null || filters.gpaMax !== null) && (
                            <Typography variant="body2">
                              GPA: {filters.gpaMin !== null ? filters.gpaMin.toFixed(2) : "Any"} -{" "}
                              {filters.gpaMax !== null ? filters.gpaMax.toFixed(2) : "Any"}
                            </Typography>
                          )}
                          {filters.skills.length > 0 && (
                            <Box>
                              <Typography sx={{ mb: 0.5 }} variant="body2">
                                Skills:
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {filters.skills.map((skill, i) => (
                                  <Chip key={i} color="primary" label={skill} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                          {filters.certifications.length > 0 && (
                            <Box>
                              <Typography sx={{ mb: 0.5 }} variant="body2">
                                Certifications:
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {filters.certifications.map((cert, i) => (
                                  <Chip key={i} color="secondary" label={cert} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {filters.otherKeywords.length > 0 && (
                      <>
                        <Typography color="text.secondary" gutterBottom variant="subtitle2">
                          Keywords
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                          {filters.otherKeywords.map((kw, i) => (
                            <Chip key={i} label={kw} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </>
                    )}

                    {!showFilters && filters.otherKeywords.length === 0 && (
                      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                        No other filters applied
                      </Typography>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Typography color="text.secondary" variant="caption">
                      {search.timestamp
                        ? DateTime.fromISO(search.timestamp).toFormat("MMM d, yyyy 'at' h:mm a")
                        : "Unknown time"}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export const CandidateSearchAnalytics = ({
  title = defaultTitle,
  subtitle = defaultSubtitle,
  data,
  status,
  onTimeRangeChange,
  timeRange,
}: CandidateSearchAnalyticsProps) => {
  const CHART_VIEW_STORAGE_KEY = "candidateSearchAnalytics.chartView";
  const KEYWORD_LIMIT_STORAGE_KEY = "candidateSearchAnalytics.keywordLimit";

  const getInitialChartView = (): ChartView => {
    const stored = localStorage.getItem(CHART_VIEW_STORAGE_KEY);
    if (
      stored === "volume" ||
      stored === "keywords" ||
      stored === "filters" ||
      stored === "hourOfDay" ||
      stored === "dayOfWeek"
    ) {
      return stored;
    }
    return "volume";
  };

  const getInitialKeywordLimit = (): KeywordLimit => {
    const stored = localStorage.getItem(KEYWORD_LIMIT_STORAGE_KEY);
    if (stored === "all") {
      return "all";
    }
    const parsed = parseInt(stored || "", 10);
    if (parsed === 10 || parsed === 25 || parsed === 50) {
      return parsed;
    }
    return 25;
  };

  const [chartView, setChartView] = useState<ChartView>(getInitialChartView);
  const [keywordLimit, setKeywordLimit] = useState<KeywordLimit>(getInitialKeywordLimit);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [selectedKeywordType, setSelectedKeywordType] = useState<"skill" | "certification" | "keyword" | null>(null);
  const [selectedFilterType, setSelectedFilterType] = useState<FilterType | null>(null);
  const [isDateSlideoutOpen, setIsDateSlideoutOpen] = useState(false);
  const [isKeywordSlideoutOpen, setIsKeywordSlideoutOpen] = useState(false);
  const [isFilterSlideoutOpen, setIsFilterSlideoutOpen] = useState(false);
  const loading = status === "pending";
  const error = status === "error";

  // Memoize the data to prevent creating new array references on each render
  const stableData = useMemo(() => data ?? [], [data]);

  const defaultTimeRange: DateRangeValue = {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ weeks: 1 }),
  };

  const handleTimeRangeChange = (newRange: DateRangeValue) => {
    onTimeRangeChange?.(newRange);
  };

  const handleChartViewChange = (event: SelectChangeEvent) => {
    const newView = event.target.value as ChartView;
    setChartView(newView);
    localStorage.setItem(CHART_VIEW_STORAGE_KEY, newView);
  };

  const handleKeywordLimitChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    const newLimit: KeywordLimit = value === "all" ? "all" : (parseInt(value, 10) as KeywordLimit);
    setKeywordLimit(newLimit);
    localStorage.setItem(KEYWORD_LIMIT_STORAGE_KEY, String(newLimit));
  };

  const handleDateBarClick = (date: string) => {
    setSelectedDate(date);
    setIsDateSlideoutOpen(true);
  };

  const handleKeywordBarClick = useCallback((keyword: string, type: "skill" | "certification" | "keyword") => {
    setSelectedKeyword(keyword);
    setSelectedKeywordType(type);
    setIsKeywordSlideoutOpen(true);
  }, []);

  const handleDateSlideoutClose = () => {
    setIsDateSlideoutOpen(false);
  };

  const handleKeywordSlideoutClose = () => {
    setIsKeywordSlideoutOpen(false);
  };

  const handleFilterBarClick = (filterType: FilterType) => {
    setSelectedFilterType(filterType);
    setIsFilterSlideoutOpen(true);
  };

  const handleFilterSlideoutClose = () => {
    setIsFilterSlideoutOpen(false);
  };

  const selectedSearchesByDate = useMemo(() => {
    if (!selectedDate || !data) {
      return [];
    }
    return getSearchesByDate(data, selectedDate);
  }, [selectedDate, data]);

  const selectedSearchesByKeyword = useMemo(() => {
    if (!selectedKeyword || !selectedKeywordType || !data) {
      return [];
    }
    return getSearchesByKeyword(data, selectedKeyword, selectedKeywordType);
  }, [selectedKeyword, selectedKeywordType, data]);

  const selectedSearchesByFilter = useMemo(() => {
    if (!selectedFilterType || !data) {
      return [];
    }
    return getSearchesByFilterType(data, selectedFilterType);
  }, [selectedFilterType, data]);

  const getHowToReadContent = () => {
    switch (chartView) {
      case "volume":
        return (
          <>
            <strong>Search Volume:</strong> Shows the number of candidate searches performed each day. Click on any bar
            to see the details of searches made on that date.
            <br />
            <br />
            <strong>Skills Table:</strong> Lists all skills that were searched for, ranked by frequency.
            <br />
            <strong>Certifications Table:</strong> Lists all certifications that were searched for, ranked by frequency.
            <br />
            <strong>Keywords Table:</strong> Lists all general keywords that were searched for, ranked by frequency.
            <br />
            <br />
            <em>
              Use this to identify patterns in employer search activity over time and understand what qualifications are
              most in demand.
            </em>
          </>
        );
      case "keywords":
        return (
          <>
            <strong>Popular Searches:</strong> Shows the most frequently searched skills, certifications, and keywords.
            <br />
            <strong>Skills</strong> (blue) are specific technical abilities.
            <br />
            <strong>Certifications</strong> (purple) are formal credentials.
            <br />
            <strong>Keywords</strong> (green) are general search terms.
            <br />
            <br />
            <em>Click on any bar to see the individual searches for that term.</em>
          </>
        );
      case "filters":
        return (
          <>
            <strong>Most Used Filters:</strong> Shows which search filters employers use most often when searching for
            candidates.
            <br />
            <strong>Graduation Year:</strong> Filters by expected graduation date.
            <br />
            <strong>GPA:</strong> Filters by minimum or maximum GPA requirements.
            <br />
            <br />
            <em>Click on any bar to see the searches that used that filter.</em>
          </>
        );
      case "hourOfDay":
        return (
          <>
            <strong>Activity by Hour:</strong> Shows when during the day employers are most actively searching for
            candidates.
            <br />
            <br />
            <em>
              Use this to understand peak search times - this can help with timing communications and platform
              maintenance.
            </em>
          </>
        );
      case "dayOfWeek":
        return (
          <>
            <strong>Activity by Day:</strong> Shows which days of the week have the most search activity.
            <br />
            <br />
            <em>Use this to understand weekly patterns - typically business days see more activity than weekends.</em>
          </>
        );
      default:
        return null;
    }
  };

  const renderChart = () => {
    switch (chartView) {
      case "volume":
        return (
          <Box sx={{ overflow: "hidden", width: "100%" }}>
            <SearchVolumeChart data={stableData} loading={loading} onBarClick={handleDateBarClick} />
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={{ xl: 4, xs: 12 }}>
                <SingleTypeTable
                  data={stableData}
                  loading={loading}
                  storageKey={SKILLS_ONLY_TABLE_NAMES_KEY}
                  title="Skills"
                  type="Skill"
                />
              </Grid>
              <Grid size={{ xl: 4, xs: 12 }}>
                <SingleTypeTable
                  data={stableData}
                  loading={loading}
                  storageKey={CERTS_ONLY_TABLE_NAMES_KEY}
                  title="Certifications"
                  type="Certification"
                />
              </Grid>
              <Grid size={{ xl: 4, xs: 12 }}>
                <SingleTypeTable
                  data={stableData}
                  loading={loading}
                  storageKey={KEYWORDS_ONLY_TABLE_NAMES_KEY}
                  title="Keywords"
                  type="Keyword"
                />
              </Grid>
            </Grid>
          </Box>
        );
      case "keywords":
        return (
          <Box sx={{ overflow: "hidden", width: "100%" }}>
            <Grid container spacing={2}>
              <Grid size={{ xl: 6, xs: 12 }}>
                <PopularKeywordsChart
                  data={stableData}
                  limit={keywordLimit}
                  loading={loading}
                  onBarClick={handleKeywordBarClick}
                />
              </Grid>
              <Grid size={{ xl: 6, xs: 12 }}>
                <SkillsCertsTable data={stableData} loading={loading} />
              </Grid>
            </Grid>
          </Box>
        );
      case "filters":
        return (
          <Box sx={{ overflow: "hidden", width: "100%" }}>
            <Grid container spacing={2}>
              <Grid size={{ xl: 6, xs: 12 }}>
                <MostUsedFiltersChart data={stableData} loading={loading} onBarClick={handleFilterBarClick} />
              </Grid>
              <Grid size={{ xl: 6, xs: 12 }}>
                <SkillsCertsTable data={stableData} loading={loading} />
              </Grid>
            </Grid>
          </Box>
        );
      case "hourOfDay":
        return (
          <Box sx={{ overflow: "hidden", width: "100%" }}>
            <HourOfDayBarChart data={stableData} loading={loading} />
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={{ xl: 6, xs: 12 }}>
                <HourOfDayPieChart data={stableData} loading={loading} />
              </Grid>
              <Grid size={{ xl: 6, xs: 12 }}>
                <HourOfDayTable data={stableData} loading={loading} />
              </Grid>
            </Grid>
          </Box>
        );
      case "dayOfWeek":
        return (
          <Box sx={{ overflow: "hidden", width: "100%" }}>
            <DayOfWeekBarChart data={stableData} loading={loading} />
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={{ xl: 6, xs: 12 }}>
                <DayOfWeekPieChart data={stableData} loading={loading} />
              </Grid>
              <Grid size={{ xl: 6, xs: 12 }}>
                <DayOfWeekTable data={stableData} loading={loading} />
              </Grid>
            </Grid>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Box sx={{ mb: 2, overflow: "hidden", pt: 1, width: "100%" }}>
        {/* How to Read This Data - Dynamic based on chart view */}
        <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, mb: 3, p: 2 }}>
          <Typography sx={{ mb: 1 }} variant="h6">
            How to Read This Data
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {getHowToReadContent()}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Box
              sx={{
                alignItems: "flex-start",
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ minWidth: 200 }}>
                <Typography sx={{ mb: 0.5 }} variant="h6">
                  {title}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {subtitle}
                </Typography>
              </Box>
              <Box sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="chart-view-label">View</InputLabel>
                  <Select
                    id="chart-view-select"
                    label="View"
                    labelId="chart-view-label"
                    onChange={handleChartViewChange}
                    value={chartView}
                  >
                    {chartViewOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {chartView === "keywords" && (
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel id="keyword-limit-label">Show</InputLabel>
                    <Select
                      id="keyword-limit-select"
                      label="Show"
                      labelId="keyword-limit-label"
                      onChange={handleKeywordLimitChange}
                      value={String(keywordLimit)}
                    >
                      <MenuItem value="10">Top 10</MenuItem>
                      <MenuItem value="25">Top 25</MenuItem>
                      <MenuItem value="50">Top 50</MenuItem>
                      <MenuItem value="all">All</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ width: 240 }}>
                  <PopoverDateRangePicker
                    onChange={handleTimeRangeChange}
                    size="small"
                    value={timeRange ?? defaultTimeRange}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid size={12}>
            {error ? (
              <Box sx={{ alignItems: "center", display: "flex", height: 300, justifyContent: "center" }}>
                <Typography color="error">Failed to load search analytics</Typography>
              </Box>
            ) : (
              renderChart()
            )}
          </Grid>

          <Grid size={12}>
            <Typography color="text.secondary" variant="caption">
              {data?.length ?? 0} search events in selected period
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <SearchDetailsSlideout
        open={isDateSlideoutOpen}
        onClose={handleDateSlideoutClose}
        selectedDate={selectedDate}
        searches={selectedSearchesByDate}
      />

      <KeywordDetailsSlideout
        open={isKeywordSlideoutOpen}
        onClose={handleKeywordSlideoutClose}
        keyword={selectedKeyword}
        keywordType={selectedKeywordType}
        searches={selectedSearchesByKeyword}
      />

      <FilterDetailsSlideout
        open={isFilterSlideoutOpen}
        onClose={handleFilterSlideoutClose}
        filterType={selectedFilterType}
        searches={selectedSearchesByFilter}
      />
    </>
  );
};
